/**
 * EditCountryModal
 * Modal for editing an existing country
 */

import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Textarea } from '@/components/ui';
import { updateCountry } from '../../services/hierarchicalLocationService';
import type { Region, Country } from '@simcheck/shared-types';

interface EditCountryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  country: Country | null;
  regions: Region[];
}

export const EditCountryModal: React.FC<EditCountryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  country,
  regions,
}) => {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [regionId, setRegionId] = useState('');
  const [flagEmoji, setFlagEmoji] = useState('');
  const [timezone, setTimezone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form when country changes
  useEffect(() => {
    if (country) {
      setName(country.name || '');
      setCode(country.code || '');
      setRegionId(country.regionId || '');
      setFlagEmoji(country.flag || '');
      setTimezone(country.timezone || '');
    }
  }, [country]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!country) {
      setError('No country selected');
      return;
    }

    if (!name.trim() || !code.trim() || !regionId) {
      setError('Name, code, and region are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await updateCountry(country.id, {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        regionId,
        flag: flagEmoji.trim() || undefined,
        timezone: timezone.trim() || undefined,
      });

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to update country:', err);
      setError(err instanceof Error ? err.message : 'Failed to update country');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Country"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-status-error-bg border border-status-error/30 rounded-lg">
            <p className="text-sm text-status-error">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Country Name *
          </label>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Germany, United States"
            disabled={loading}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Country Code *
          </label>
          <Input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g., DE, US"
            maxLength={3}
            disabled={loading}
            required
          />
          <p className="text-xs text-muted-foreground/70 mt-1">
            ISO 3166-1 alpha-2 code
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Region *
          </label>
          <select
            value={regionId}
            onChange={(e) => setRegionId(e.target.value)}
            disabled={loading}
            required
            className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-foreground"
          >
            <option value="">Select a region...</option>
            {regions.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name} ({region.code})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Flag Emoji
          </label>
          <Input
            type="text"
            value={flagEmoji}
            onChange={(e) => setFlagEmoji(e.target.value)}
            placeholder="e.g., 🇩🇪, 🇺🇸"
            maxLength={4}
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Timezone
          </label>
          <Input
            type="text"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="e.g., Europe/Berlin, America/New_York"
            disabled={loading}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            disabled={loading || !name.trim() || !code.trim() || !regionId}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
